import { useEffect, useRef, useState, type FC } from 'react';
import { PAYWALL_BACKDROP_Z_INDEX, PAYWALL_Z_INDEX } from 'Constants/StackingLevels';
import { Button, Box, TextField, Link, useMediaQuery } from '@mui/material';
import '../TrialWelcome/trial_welcome.css';
import './paywall.css';
import CloseIcon from '../../assets/images/CloseIcon.png';
import GreenCheckmark from '../../assets/images/GreenCheckmark.png';
import {
  PAYWALL_COPY,
  availableWhenTrialEndsMessage,
  noCardTrialEnd,
  paywallReasonFor,
  type TrialBillingDetails,
} from './paywallCopy';
import {
  PLAN_IDS,
  PLAN_DETAILS,
  formatPrice,
  savingsPercent,
  type PlanId,
  type PricingCatalog,
} from 'Billing/Catalog';
import {
  couponAcceptedLine,
  couponCoversNoPlan,
  parseCouponValidation,
  planDiscount,
  type CouponValidation,
} from 'Billing/Coupon';
import { usePricingCatalog } from 'Billing/usePricingCatalog';
import { billingErrorMessage } from 'Billing/billingErrorMessage';
import { storeOrderErrorMessage, storeOrderErrorReport } from 'Billing/storeOrderErrorMessage';
import axios from 'axios';
import WelcomeButton from '../TrialWelcome/WelcomeButton';
import LogOutButton from 'Components/LogOutButton';
import { SURVEY_LINK, isCapacitorNative } from 'Utils/Constants';
import { useSelector, useDispatch } from 'react-redux';
import { MainAppReducer } from 'Types';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import {
  IAP_VERIFICATION_EVENT,
  armIAPReceiptVerification,
  clearIAPReceiptVerificationArm,
  ensureIAPReady,
  iapProductIdForPlan,
  nativeIAPStorePlatform,
  restoreIAPPurchases,
} from 'iap/initPurchases';
import {
  APPLE_ID_HOLDS_PLAN_MESSAGE,
  APPLE_ID_HOLDS_PLAN_RESTORE_LEAD,
  appleIdHoldsProduct,
  selectRecurringOffer,
  type RecurringOffer,
} from 'iap/Offers';
import { appStoreAccountToken } from 'iap/accountToken';
import { reportError } from 'Utils/reportError';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const TERMS_OF_USE_URL = 'https://www.museflow.ai/terms';
const PRIVACY_POLICY_URL = 'https://www.museflow.ai/privacy';
interface PaywallProps {
  setShowPaywall: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  setPlanTypeSelected: React.Dispatch<React.SetStateAction<string>>;
  exitAllowed: boolean;
  discountCodeValid: boolean | null;
  setDiscountCodeValid: React.Dispatch<React.SetStateAction<boolean | null>>;
  discountCode: string;
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>;
  showDiscountCode: boolean;
  setShowDiscountCode: React.Dispatch<React.SetStateAction<boolean>>;
  /** Only supplied by the development-only visual route; all purchase controls stay disabled. */
  previewCatalog?: PricingCatalog;
}

/**
 * The validate route itself refused the code (it answers 400 for an unknown or used-up code), as
 * opposed to not being able to answer. A 401 or 403 is not a refusal: with no token at hand the
 * request goes out bare and comes back 401 after the one refresh-and-retry, which says nothing
 * about the code.
 */
const validationRefused = (error: unknown) => {
  const status = Number((error as { response?: { status?: unknown } } | null)?.response?.status);
  return status === 400 || status === 422;
};

/** A saved code the server could not be asked about is asked again after these waits. */
const SAVED_CODE_RETRY_DELAYS_MS = [2000, 6000];

/** When the account's no-card free trial ends, or null when billing cannot say (MUS-614). */
async function fetchNoCardTrialEnd(token: string | undefined): Promise<Date | null> {
  try {
    const { data } = await axios.get<{ details?: TrialBillingDetails }>(
      `${baseUrl}stripe/user-billing-details`,
      { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
    );
    return noCardTrialEnd(data?.details, new Date());
  } catch {
    return null;
  }
}

const Paywall: FC<PaywallProps> = ({
  setShowPaywall,
  exitAllowed,
  discountCodeValid,
  setDiscountCodeValid,
  discountCode,
  setDiscountCode,
  showDiscountCode,
  setShowDiscountCode,
  previewCatalog,
}) => {
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const dispatch = useDispatch();
  // Android's Back closes a paywall the user may leave, as its X does. A paywall the user must
  // pass has no X and holds Back for the app instead (PaymentFlow).
  useHardwareBackClosesLayer(exitAllowed, () => setShowPaywall(false), PAYWALL_Z_INDEX);
  const currentAccount = useRef(userData?.user_id);
  currentAccount.current = userData?.user_id;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const accountIsCurrent = () => mounted.current && currentAccount.current === userData?.user_id;

  const { currentAuthToken } = useAuthWrapperContext();
  const platform = nativeIAPStorePlatform();
  const native = isCapacitorNative();
  const webPricing = usePricingCatalog(!native, previewCatalog);
  const [nativeOffers, setNativeOffers] = useState<Partial<Record<PlanId, RecurringOffer>>>({});
  // Plans the App Store account on this device already holds (MUS-634): the store will not sell
  // them again, so they say so instead of reading "Unavailable".
  const [heldPlans, setHeldPlans] = useState<Partial<Record<PlanId, boolean>>>({});
  // The server sells no store plan while the account's free trial runs (MUS-614): each plan says
  // when it will, instead of reading "Unavailable".
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [nativeLoading, setNativeLoading] = useState(native);
  const [nativeRevision, setNativeRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // A purchase check that a retry cannot get past (MUS-618): no Retry button beside it and no
  // "Restore requested" line under it.
  const [finalNotice, setFinalNotice] = useState<string | null>(null);
  const finalNoticeArrived = useRef(false);
  const [purchasingPlan, setPurchasingPlan] = useState<string | null>(null);
  const busy = useRef(false);
  const [validatingCode, setValidatingCode] = useState(false);
  // MUS-639: opening the coupon field by hand moves to it. The field and Apply can sit below
  // the fold of a laptop-height paywall, with no visible scroll bar to say so.
  const couponOpenedByHand = useRef(false);
  const couponInput = useRef<HTMLInputElement>(null);
  const couponContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showDiscountCode || !couponOpenedByHand.current) return;
    couponOpenedByHand.current = false;
    couponInput.current?.focus();
    couponContainer.current?.scrollIntoView?.({ block: 'nearest' });
  }, [showDiscountCode]);
  // What the server said about the code in the field (MUS-629); cleared when the code is edited.
  const [coupon, setCoupon] = useState<CouponValidation | null>(null);
  // A saved code the server could not be asked about: pending, still sent to checkout, which checks it.
  const [couponUnchecked, setCouponUnchecked] = useState(false);
  // A typed code the server could not be asked about: neither refused nor sent.
  const [couponCheckFailed, setCouponCheckFailed] = useState(false);
  const savedCodeRetries = useRef(0);
  const savedCodeRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedCode = userData?.coupon_code?.trim() || null;
  const fieldHoldsSavedCode = !!savedCode && discountCode.trim() === savedCode;
  // The reason is fixed when the paywall opens, so the heading cannot change under the reader.
  const [reason] = useState(() => paywallReasonFor(userData, new Date()));
  const copy = PAYWALL_COPY[reason];
  // On a phone held sideways the plans fill the screen and the restore control below them is
  // out of sight (MUS-598), so there it sits in the opening text instead.
  const smallLandscapeScreen = useMediaQuery('(orientation: landscape) and (max-height: 520px)');

  useEffect(() => {
    const receiveError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; retryable?: boolean }>).detail;
      if (!detail?.message) return;
      setError(detail.message);
      if (detail.retryable === false) {
        finalNoticeArrived.current = true;
        setFinalNotice(detail.message);
        setMessage(null);
      }
    };
    window.addEventListener(IAP_VERIFICATION_EVENT, receiveError);
    return () => window.removeEventListener(IAP_VERIFICATION_EVENT, receiveError);
  }, []);

  useEffect(() => {
    if (!native) return;
    let canceled = false;
    setNativeLoading(true);
    setNativeOffers({});
    setHeldPlans({});
    setTrialEndsAt(null);
    setError(null);
    (async () => {
      try {
        if (!platform) throw new Error('Purchases are unavailable on this device.');
        const token = await currentAuthToken();
        const { data: options } = await axios.get<{
          ready: boolean;
          accountId?: string;
          reason?: string;
        }>(`${baseUrl}${platform === 'google' ? 'google' : 'receipts'}/purchase-options`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!options.ready || (platform === 'google' && !options.accountId)) {
          // A refusal with a reason, on an account whose no-card trial is running, is the trial's:
          // a retry cannot help, and the plans go on sale when it ends.
          const trialEnd =
            !options.ready && options.reason && userData?.subscription_status === 'trialing'
              ? await fetchNoCardTrialEnd(token)
              : null;
          if (trialEnd) {
            if (!canceled) setTrialEndsAt(trialEnd);
            return;
          }
          throw new Error(
            options.reason ||
              'Store subscriptions are temporarily unavailable. Please try again later.',
          );
        }
        const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
        if (!Cdv) throw new Error('The store is unavailable. Please try again.');
        const storePlatform =
          platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
        const offers: Partial<Record<PlanId, RecurringOffer>> = {};
        const held: Partial<Record<PlanId, boolean>> = {};
        for (const plan of PLAN_IDS) {
          const product = Cdv.store.get(iapProductIdForPlan(plan, platform), storePlatform);
          offers[plan] = selectRecurringOffer(product, plan);
          held[plan] =
            platform === 'apple' &&
            !offers[plan] &&
            appleIdHoldsProduct(product, product && Cdv.store.findInLocalReceipts?.(product));
        }
        if (!canceled) {
          setNativeOffers(offers);
          setHeldPlans(held);
          // A retry cannot bring back a plan the store account already holds.
          if (PLAN_IDS.some((plan) => !offers[plan] && !held[plan]))
            setError('Some subscriptions are unavailable from the store. Please try again later.');
        }
      } catch (cause) {
        if (!canceled) setError(billingErrorMessage(cause));
      } finally {
        if (!canceled) setNativeLoading(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [native, platform, nativeRevision, currentAuthToken, dispatch, userData?.user_id]);

  const cancelSavedCodeRetry = () => {
    if (savedCodeRetryTimer.current) clearTimeout(savedCodeRetryTimer.current);
    savedCodeRetryTimer.current = null;
  };
  useEffect(() => cancelSavedCodeRetry, []);

  /**
   * Asks the server about the code in the field. A code saved on the account, or one already
   * accepted, is only given up when the server refuses it (`keepUnlessRefused`): it goes to
   * checkout, which checks it again, rather than being lost to a failure to answer.
   */
  const validateDiscountCode = async (keepUnlessRefused = false, automatic = false) => {
    if (previewCatalog || validatingCode || purchasingPlan) return;
    cancelSavedCodeRetry();
    setValidatingCode(true);
    // A re-ask nobody pressed for leaves alone a message the person may be reading.
    if (!automatic) setError(null);
    setCouponCheckFailed(false);
    try {
      const authToken = await currentAuthToken();
      const { data } = await axios.post<unknown>(
        `${baseUrl}stripe/validate-coupon-code`,
        { code: discountCode.trim() },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 15000,
        },
      );
      if (!mounted.current) return;
      const answer = parseCouponValidation(data);
      setCoupon(answer);
      setCouponUnchecked(false);
      savedCodeRetries.current = 0;
      setDiscountCode(discountCode.trim());
      // A code that takes nothing off these plans would only be refused at checkout.
      setDiscountCodeValid(!(answer && couponCoversNoPlan(answer)));
    } catch (cause) {
      if (!mounted.current) return;
      setCoupon(null);
      if (validationRefused(cause)) {
        setCouponUnchecked(false);
        setDiscountCodeValid(false);
      } else if (keepUnlessRefused) {
        // Pending, not refused: kept for checkout and asked about again, by itself a few times
        // (a token that was not ready when the paywall opened usually is a moment later).
        setCouponUnchecked(true);
        setDiscountCodeValid(true);
        const delay = SAVED_CODE_RETRY_DELAYS_MS[savedCodeRetries.current];
        if (delay !== undefined) {
          savedCodeRetries.current += 1;
          savedCodeRetryTimer.current = setTimeout(
            () => latestValidate.current(keepUnlessRefused, true),
            delay,
          );
        }
      } else {
        setDiscountCodeValid(null);
        setCouponCheckFailed(true);
      }
    } finally {
      if (mounted.current) setValidatingCode(false);
    }
  };
  const latestValidate = useRef(validateDiscountCode);
  latestValidate.current = validateDiscountCode;

  // A code stored on the account is asked about like a typed one as soon as it is in the field
  // (whether that is before or after the paywall opens), so its discount shows and an expired one
  // is caught before SELECT.
  useEffect(() => {
    if (!native && fieldHoldsSavedCode && discountCodeValid === null) validateDiscountCode(true);
  }, [fieldHoldsSavedCode, discountCodeValid]);

  // What the server said about the code is kept here, its validity in PaymentFlow: a paywall that
  // mounts again over a code already accepted asks again, so its prices and line come back.
  useEffect(() => {
    if (!native && discountCode.trim() && discountCodeValid === true) validateDiscountCode(true);
  }, []);

  const submitPlanSelect = async (plan: PlanId) => {
    if (busy.current || previewCatalog) return;
    busy.current = true;
    setPurchasingPlan(plan);
    setError(null);
    setMessage(null);
    try {
      if (native) {
        if (!platform || !nativeOffers[plan])
          throw new Error('This subscription is unavailable. Please reload prices.');
        const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
        if (!Cdv) throw new Error('The store is unavailable. Please try again.');
        const storePlatform =
          platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
        const currentOffer = selectRecurringOffer(
          Cdv.store.get(iapProductIdForPlan(plan, platform), storePlatform),
          plan,
        );
        const displayed = nativeOffers[plan]!;
        if (
          !currentOffer ||
          currentOffer.phase.priceMicros !== displayed.phase.priceMicros ||
          currentOffer.phase.currency !== displayed.phase.currency ||
          currentOffer.offer.id !== displayed.offer.id
        ) {
          setNativeRevision((value) => value + 1);
          throw new Error(
            'Store pricing changed. Please review the updated prices and select again.',
          );
        }
        // Recheck immediately before opening the store sheet: another device may have subscribed.
        const token = await currentAuthToken();
        const { data: purchaseOptions } = await axios.get<{
          ready: boolean;
          accountId?: string;
          reason?: string;
        }>(`${baseUrl}${platform === 'google' ? 'google' : 'receipts'}/purchase-options`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!purchaseOptions.ready || (platform === 'google' && !purchaseOptions.accountId)) {
          throw new Error(
            purchaseOptions.reason ||
              'Please restore or manage your existing subscription before purchasing.',
          );
        }
        if (!accountIsCurrent()) return;
        // An App Store purchase that does not carry this account's id could
        // never be matched to it if the app were interrupted after payment.
        // Refuse before any money moves rather than take it unattributed.
        if (platform === 'apple' && !appStoreAccountToken(userData)) {
          throw new Error(
            'We could not confirm your account for this purchase. Please log out, log back in and try again.',
          );
        }
        armIAPReceiptVerification(iapProductIdForPlan(plan, platform));
        const orderError = await Cdv.store.order(
          currentOffer.offer,
          platform === 'google'
            ? { googlePlay: { accountId: purchaseOptions.accountId } }
            : undefined,
        );
        if (!accountIsCurrent()) return;
        if (orderError) {
          clearIAPReceiptVerificationArm();
          if (
            Cdv.ErrorCode?.PAYMENT_CANCELLED !== undefined &&
            orderError.code === Cdv.ErrorCode.PAYMENT_CANCELLED
          )
            return;
          // The store's own code goes to error reporting; the screen gets a sentence (MUS-617).
          reportError(new Error(storeOrderErrorReport(orderError)), 'billing.purchase');
          setError(storeOrderErrorMessage(orderError));
          return;
        }
        setMessage(
          'Your store will confirm the purchase. Pending payments or approvals unlock access once confirmed.',
        );
      } else {
        if (!webPricing.catalog) throw new Error('Please reload subscription prices.');
        const token = await currentAuthToken();
        const { data } = await axios.post<{ url?: string }>(
          `${baseUrl}stripe/checkout-session`,
          {
            plan,
            // A saved code not yet asked about still goes: checkout checks it on the server.
            code:
              discountCodeValid || (discountCodeValid === null && fieldHoldsSavedCode)
                ? discountCode.trim()
                : null,
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
        );
        if (!data.url || !data.url.startsWith('https://checkout.stripe.com/'))
          throw new Error('Checkout is unavailable. Please try again.');
        window.location.assign(data.url);
      }
    } catch (cause) {
      reportError(cause, 'billing.purchase');
      setError(billingErrorMessage(cause));
    } finally {
      busy.current = false;
      setPurchasingPlan(null);
    }
  };

  const restore = async () => {
    if (busy.current) return;
    busy.current = true;
    setPurchasingPlan('restore');
    setError(null);
    setMessage(null);
    finalNoticeArrived.current = false;
    try {
      const active = await restoreIAPPurchases(userData, dispatch, currentAuthToken);
      if (!accountIsCurrent()) return;
      if (active) setShowPaywall(false);
      else if (finalNoticeArrived.current) return;
      setMessage(
        active
          ? 'Your subscription has been restored.'
          : 'Restore requested. An active purchase on this store account will unlock access after verification. If none appears, check your store subscriptions.',
      );
    } catch (cause) {
      setError(billingErrorMessage(cause));
    } finally {
      busy.current = false;
      setPurchasingPlan(null);
    }
  };

  // Upright on a phone the answer to a restore is below all three plans, out of sight of a held
  // plan's link (MUS-634), so a restore started there brings its answer into view when it settles.
  const outcomeAlert = useRef<HTMLDivElement>(null);
  const outcomeStatus = useRef<HTMLParagraphElement>(null);
  const [outcomeRevealRequest, setOutcomeRevealRequest] = useState(0);
  useEffect(() => {
    if (!outcomeRevealRequest) return;
    (outcomeAlert.current ?? outcomeStatus.current)?.scrollIntoView?.({ block: 'nearest' });
  }, [outcomeRevealRequest]);
  const restoreFromHeldPlan = async () => {
    await restore();
    if (mounted.current) setOutcomeRevealRequest((value) => value + 1);
  };

  const loading = native ? nativeLoading : webPricing.loading;
  const displayedError = error || webPricing.error;
  const reload = () => {
    setError(null);
    if (native) setNativeRevision((value) => value + 1);
    else webPricing.reload();
  };
  // With the restore control at the top, its answer has to be there too: below the plans it
  // would be off screen on the very phones the control was moved for.
  const outcomeBesideRestore = native && smallLandscapeScreen;
  const outcome = (
    <>
      {displayedError && (
        <Box className="iap-error" role="alert" ref={outcomeAlert}>
          {displayedError}{' '}
          {displayedError !== finalNotice && (
            <Button onClick={reload} disabled={loading || !!purchasingPlan}>
              Retry
            </Button>
          )}
        </Box>
      )}
      {message && (
        <p className="paywall-desc-text" role="status" ref={outcomeStatus}>
          {message}
        </p>
      )}
    </>
  );

  return (
    <>
      <Box className="modal-outer" sx={{ zIndex: PAYWALL_BACKDROP_Z_INDEX }} />
      <Box
        className={['modal-inner2', 'paywall-modal', exitAllowed && 'paywall-closable']
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a subscription"
        sx={{ zIndex: PAYWALL_Z_INDEX }}
      >
        {exitAllowed && (
          <Button
            onClick={() => setShowPaywall(false)}
            aria-label="Close"
            // Above the heading block: that block is positioned and, on a phone, as wide as
            // the dialog, so without this it lies over the button and takes its taps (MUS-605).
            sx={{ position: 'absolute', top: 15, right: 15, zIndex: 1 }}
          >
            <img src={CloseIcon} alt="" />
          </Button>
        )}
        <Box className="paywall-desc-container" data-paywall-reason={reason}>
          <h3 className="paywall-desc-title">{copy.heading}</h3>
          <p className="paywall-desc-text" style={{ marginTop: '5px', lineHeight: '133%' }}>
            {copy.lead} We’d also love to hear{' '}
            <Link href={SURVEY_LINK} target="_blank" rel="noreferrer" className="link">
              any feedback you have
            </Link>
            .
            {native && smallLandscapeScreen && (
              <>
                {' '}
                Already subscribed?{' '}
                <Link
                  component="button"
                  type="button"
                  className="link paywall-restore-inline"
                  onClick={restore}
                  disabled={!!purchasingPlan}
                >
                  {purchasingPlan === 'restore' ? 'Restoring…' : 'Restore purchases'}
                </Link>
              </>
            )}
          </p>
        </Box>
        {outcomeBesideRestore && outcome}
        <Box className="plan-container" aria-busy={loading}>
          {PLAN_IDS.map((id) => {
            const details = PLAN_DETAILS[id];
            const webPlan = webPricing.catalog?.plans.find((plan) => plan.id === id);
            const phase = nativeOffers[id]?.phase;
            const amount = native
              ? phase
                ? phase.priceMicros / 1000000
                : undefined
              : webPlan
                ? webPlan.amountCents / 100
                : undefined;
            const currency = native ? phase?.currency : webPricing.catalog?.currency;
            const monthlyPhase = nativeOffers.monthly?.phase;
            const monthly = native
              ? monthlyPhase?.currency === currency
                ? (monthlyPhase?.priceMicros ?? 0) / 1000000
                : 0
              : (webPricing.catalog?.plans.find((plan) => plan.id === 'monthly')?.amountCents ??
                  0) / 100;
            const savings = amount ? savingsPercent(amount, monthly, details.months) : 0;
            const price = native
              ? phase?.price
              : amount !== undefined && currency
                ? formatPrice(amount, currency)
                : undefined;
            const discount =
              !native && discountCodeValid && !couponUnchecked && coupon && webPlan && currency
                ? planDiscount(webPlan, currency, coupon)
                : null;
            // A trial account is refused before the store is asked about any plan, so a plan its
            // store account may hold is never known: the trial's sentence is the one that applies.
            const afterTrialMessage =
              !price && trialEndsAt ? availableWhenTrialEndsMessage(trialEndsAt) : null;
            const held = !price && !afterTrialMessage && !!heldPlans[id];
            const discountedPrice =
              discount && currency
                ? discount.amountCents === 0
                  ? 'Free'
                  : formatPrice(discount.amountCents / 100, currency)
                : undefined;
            return (
              <Box className="plan" key={id} data-plan={id}>
                {/* Beside a coupon price a savings percentage would read as the coupon's. */}
                {savings > 0 && !discount && (
                  <Box className="discount-percent">
                    <p className="discount-percent-text">{savings}% off</p>
                  </Box>
                )}
                <Box className="plan-card">
                  <Box className="plan-title">
                    <p className="plan-text-desc">{details.title}</p>
                    {discountedPrice ? (
                      <p className="plan-text-price">
                        <span className="paywall-sr-only">
                          was {price}, now {discountedPrice}
                        </span>
                        <span aria-hidden="true">
                          <s className="plan-text-original-price">{price}</s> {discountedPrice}
                        </span>
                      </p>
                    ) : afterTrialMessage ? (
                      <p className="plan-text-time plan-text-after-trial">{afterTrialMessage}</p>
                    ) : held ? (
                      <p className="plan-text-time plan-text-held">
                        {APPLE_ID_HOLDS_PLAN_MESSAGE}
                        {/* Held sideways, Restore purchases is already in view in the opening
                            text; upright, it is below all three plans, out of sight on a phone. */}
                        {!smallLandscapeScreen && (
                          <>
                            {' '}
                            {APPLE_ID_HOLDS_PLAN_RESTORE_LEAD}{' '}
                            <Link
                              component="button"
                              type="button"
                              className="link paywall-restore-inline paywall-held-restore"
                              onClick={restoreFromHeldPlan}
                              disabled={!!purchasingPlan}
                            >
                              {purchasingPlan === 'restore' ? 'Restoring…' : 'Restore purchases'}
                            </Link>
                            .
                          </>
                        )}
                      </p>
                    ) : (
                      <p className="plan-text-price">
                        {price ?? (loading ? 'Loading…' : 'Unavailable')}
                      </p>
                    )}
                    {discount?.amountCents !== 0 && !held && !afterTrialMessage && (
                      <p
                        className={`plan-text-time${discount ? ' plan-text-discounted-time' : ''}`}
                      >
                        per {details.frequency}
                      </p>
                    )}
                    {/* A per-month figure beside a discounted price would misstate the payments
                        after the discount ends, so a discounted plan shows how long it lasts. */}
                    {discount && <p className="plan-text-discount">{discount.duration}</p>}
                    {!discount && details.months > 1 && amount && currency && (
                      <p className="plan-text-equivalent">
                        {formatPrice(amount / details.months, currency)} per month equivalent
                      </p>
                    )}
                    {id !== 'monthly' && (
                      <Box className="lesson-check" sx={{ width: 'unset' }}>
                        <img src={GreenCheckmark} className="green-check-img" alt="" />
                        <p className="plan-text-time">
                          {id === 'annual' ? 'Two 30min lessons' : '30min lesson'}
                        </p>
                      </Box>
                    )}
                    {id === 'annual' && (
                      <Box className="lesson-check">
                        <img src={GreenCheckmark} className="green-check-img" alt="" />
                        <p className="plan-text-time">MIDI cable</p>
                      </Box>
                    )}
                  </Box>
                  <WelcomeButton
                    className="plan-select-button"
                    onClick={() => submitPlanSelect(id)}
                    disabled={
                      !!purchasingPlan || validatingCode || !price || loading || !!previewCatalog
                    }
                  >
                    {purchasingPlan === id ? 'LOADING…' : 'SELECT'}
                  </WelcomeButton>
                </Box>
              </Box>
            );
          })}
        </Box>
        <p className="paywall-desc-text billing-terms">
          Automatically renews until canceled. Savings compare with the monthly plan.{' '}
          {native
            ? 'The store confirms your price and billing terms.'
            : 'Prices are in USD; applicable tax and coupon terms are shown at checkout.'}
          {/* MUS-627: a subscription screen must link to both. They are plain new-window links
              on purpose: in the store apps the app shell passes a new-window link to the
              device's own browser, which the in-app browser sheet would not be. */}
          <span className="paywall-legal-links">
            <Link
              href={TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="paywall-legal-link"
            >
              Terms of Use (EULA)
            </Link>
            <span aria-hidden="true"> · </span>
            <Link
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="paywall-legal-link"
            >
              Privacy Policy
            </Link>
          </span>
        </p>
        {!outcomeBesideRestore && outcome}
        {native ? (
          !smallLandscapeScreen && (
            <Button
              onClick={restore}
              disabled={!!purchasingPlan}
              className="paywall-restore-button"
            >
              {purchasingPlan === 'restore' ? 'RESTORING…' : 'Restore purchases'}
            </Button>
          )
        ) : (
          <>
            {!showDiscountCode && (
              <Button
                onClick={() => {
                  couponOpenedByHand.current = true;
                  setShowDiscountCode(true);
                }}
                className="discount-code"
              >
                Got a coupon code?
              </Button>
            )}
            {showDiscountCode && (
              <Box className="discount-container" ref={couponContainer}>
                <TextField
                  inputRef={couponInput}
                  className={`discount-input ${discountCodeValid && !couponUnchecked ? 'valid-discount' : ''}`}
                  label="Enter Coupon Code"
                  value={discountCode}
                  disabled={validatingCode || !!purchasingPlan}
                  onChange={(event) => {
                    setDiscountCode(event.target.value);
                    setDiscountCodeValid(null);
                    setCoupon(null);
                    setCouponUnchecked(false);
                    setCouponCheckFailed(false);
                    cancelSavedCodeRetry();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !validatingCode)
                      validateDiscountCode(fieldHoldsSavedCode || couponUnchecked);
                  }}
                />
                <Button
                  onClick={() => validateDiscountCode(fieldHoldsSavedCode || couponUnchecked)}
                  className="discount-button"
                  disabled={validatingCode || !discountCode.trim() || !!previewCatalog}
                >
                  {validatingCode ? 'Checking…' : 'Apply'}
                </Button>
                {discountCodeValid === false && (
                  <span role="alert" className="iap-error">
                    {coupon && couponCoversNoPlan(coupon)
                      ? 'This coupon does not apply to these plans.'
                      : 'Invalid or unavailable coupon.'}
                  </span>
                )}
                {couponCheckFailed && (
                  <span role="alert" className="iap-error">
                    This code could not be checked just now. Please try again.
                  </span>
                )}
              </Box>
            )}
            {discountCodeValid && couponUnchecked && (
              <p className="paywall-desc-text" role="status">
                Coupon pending: it could not be checked just now. It is still sent to checkout,
                which applies it if it is valid.{' '}
                <Button onClick={() => validateDiscountCode(true)} disabled={validatingCode}>
                  {validatingCode ? 'Checking…' : 'Retry'}
                </Button>
              </p>
            )}
            {discountCodeValid && !couponUnchecked && (
              <p className="paywall-desc-text" role="status">
                {coupon
                  ? couponAcceptedLine(coupon)
                  : 'Coupon accepted. Your discount is applied at checkout.'}
              </p>
            )}
          </>
        )}
        <Box className="logout-container">
          <LogOutButton />
        </Box>
      </Box>
    </>
  );
};
export default Paywall;
